import { useState, useEffect, useContext, useRef } from "react";
import "../styles/application-sidebar.css";
import "../styles/image-preview.css";
import api from "../services/api-service";
import { AuthContext } from "../contexts/AuthContext";
import { useAlert } from "../contexts/AlertContext";

export default function ReportSidebar({ isOpen, onClose, position }) {
  const [description, setDescription] = useState("");

  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);

  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);

  const [type, setType] = useState("");
  const [subtype, setSubtype] = useState("");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await api.get("/types");
        setTypes(res.data);
      } catch (err) {
        console.error("Failed to fetch types", err);
      }
    };

    fetchTypes();
  }, []);

  const handleTypeChange = async (typeId) => {
    setType(typeId);
    setSubtype("");

    if (!typeId) {
      setSubtypes([]);
      return;
    }

    try {
      const res = await api.get(`/types/${typeId}/subtypes`);
      setSubtypes(res.data);
    } catch (err) {
      console.error("Failed to fetch subtypes", err);
      setSubtypes([]);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const total = images.length + files.length;

    if (total > 10) {
      showAlert("Maximum 10 images allowed", "error");
      return;
    }

    setImages((prev) => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setUploading(true);

      const formData = new FormData();

      formData.append("description", description);
      formData.append("typeId", type);

      if (subtype) {
        formData.append("subtypeId", subtype);
      }

      if (user?.id) {
        formData.append("userId", String(user.id));
      }

      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${position[0]}&lon=${position[1]}&format=json`,
      );
      const data = await res.json();
      const addr = data.display_name || null;

      const neigh =
        data.address.quarter?.trim() ||
        data.address.neighbourhood?.trim() ||
        null;

      formData.append("location.address", addr);
      formData.append("location.neighbourhood", neigh);

      formData.append("location.latitude", String(position[0]));
      formData.append("location.longitude", String(position[1]));

      images.forEach((image) => {
        formData.append("images", image);
      });

      await api.post("/incidents", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      showAlert("Incident reported successfully!", "success");

      setDescription("");
      setType("");
      setSubtype("");
      setSubtypes([]);
      setImages([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onClose();
    } catch (err) {
      console.error("Failed to submit incident", err);
      showAlert("Failed to report incident. Please try again.", "error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div
        className={`sidebar-overlay ${isOpen ? "active" : ""}`}
        onClick={onClose}
      />

      <div className={`sidebar ${isOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <h2>Report Incident</h2>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* <div className="location-preview">
          <p>
            <strong>Reporting at:</strong>
          </p>  
          <p>
            {position[0].toFixed(5)}, {position[1].toFixed(5)}
          </p>
        </div> */}

        <form className="incident-form" onSubmit={handleSubmit}>
          <label>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what happened..."
            required
          />

          <label>Type</label>
          <select
            value={type}
            onChange={(e) => handleTypeChange(e.target.value)}
            required
          >
            <option value="">Select type</option>
            {types?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>

          {subtypes.length > 0 && (
            <>
              <label>Subtype (optional)</label>
              <select
                value={subtype}
                onChange={(e) => setSubtype(e.target.value)}
              >
                <option value="">Select subtype</option>
                {subtypes.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </>
          )}

          <label>Images (max 10)</label>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageChange}
            disabled={images.length >= 10 || uploading}
            required
          />

          <p>{images.length}/10 selected</p>

          <div className="image-preview">
            {images.map((img, index) => (
              <div key={index} className="image-item">
                <img src={URL.createObjectURL(img)} alt="preview" />
                <button type="button" onClick={() => removeImage(index)}>
                  ✕
                </button>
              </div>
            ))}
          </div>

          <button type="submit" className="submit-btn" disabled={uploading}>
            {uploading ? "Uploading..." : "Submit Report"}
          </button>
        </form>
      </div>
    </>
  );
}
